import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminService } from '../../services/loanService';
import { formatDate } from '../../utils/helpers';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const SEVERITY_META = {
  low:      { color: '#94a3b8' },
  medium:   { color: '#5B8EFF' },
  high:     { color: '#ff9900' },
  critical: { color: '#FF4757' },
};

const STATUS_META = {
  open:          { label: 'Open',          color: '#FF4757' },
  investigating: { label: 'Investigating', color: '#ff9900' },
  confirmed:     { label: 'Confirmed',     color: '#818CF8' },
  dismissed:     { label: 'Dismissed',     color: '#94a3b8' },
  escalated:     { label: 'Escalated',     color: '#F5A623' },
};

function SeverityDot({ value }) {
  const c = (SEVERITY_META[value] || {}).color || '#94a3b8';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: c, boxShadow: `0 0 6px ${c}` }} />
      <span style={{ fontSize: '0.75rem', fontWeight: 700, color: c, textTransform: 'uppercase' }}>{value}</span>
    </span>
  );
}

function StatusBadge({ value }) {
  const m = STATUS_META[value] || { label: value, color: '#94a3b8' };
  return (
    <span style={{
      fontSize: '0.65rem', fontWeight: 700, padding: '0.2rem 0.6rem',
      borderRadius: 99, textTransform: 'uppercase',
      background: `${m.color}18`, color: m.color, border: `1px solid ${m.color}30`,
    }}>{m.label}</span>
  );
}

function UpdateModal({ alert, onClose }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState(alert.status);
  const [notes, setNotes] = useState(alert.resolution_notes || '');
  const [blacklist, setBlacklist] = useState(alert.is_blacklisted);
  const [err, setErr] = useState('');

  const mutation = useMutation({
    mutationFn: (d) => adminService.updateFraudAlert(alert.id, d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-fraud'] }); onClose(); },
    onError: (e) => setErr(e.response?.data?.message || 'Failed.'),
  });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="card" style={{ width: 460 }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.25rem' }}>Update Fraud Alert</h2>
        <p style={{ fontSize: '0.8rem', color: 'var(--text-2)', marginBottom: '1rem' }}>{alert.reference} — <SeverityDot value={alert.severity} /></p>

        <div className="form-group">
          <label className="form-label">Status</label>
          <select className="form-input" value={status} onChange={e => setStatus(e.target.value)}>
            {Object.entries(STATUS_META).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Resolution Notes</label>
          <textarea className="form-input" rows={3} value={notes} onChange={e => setNotes(e.target.value)} />
        </div>
        <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <input type="checkbox" id="blacklist" checked={blacklist} onChange={e => setBlacklist(e.target.checked)} />
          <label htmlFor="blacklist" style={{ fontSize: '0.875rem', color: 'var(--text-1)', cursor: 'pointer' }}>Blacklist subject user</label>
        </div>

        {err && <div className="alert alert-error"><span>⚠</span><span>{err}</span></div>}

        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
          <button className="btn btn-primary" style={{ flex: 1 }} disabled={mutation.isPending}
            onClick={() => mutation.mutate({ status, resolution_notes: notes, is_blacklisted: blacklist })}>
            {mutation.isPending ? 'Updating…' : 'Update Alert'}
          </button>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function FraudAlerts() {
  const [severityFilter, setSeverityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-fraud', severityFilter, statusFilter],
    queryFn: () => adminService.getFraudAlerts({
      ...(severityFilter && { severity: severityFilter }),
      ...(statusFilter && { status: statusFilter }),
    }).then(r => r.data.data),
  });

  const alerts = data?.results || data || [];

  return (
    <div>
      <div className="page-header">
        <h1>Fraud & Compliance</h1>
        <p>Monitor fraud alerts — high/critical alerts require checker confirmation</p>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        <select className="form-input" style={{ width: 140 }} value={severityFilter} onChange={e => setSeverityFilter(e.target.value)}>
          <option value="">All Severities</option>
          {Object.keys(SEVERITY_META).map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
        </select>
        <select className="form-input" style={{ width: 160 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Statuses</option>
          {Object.entries(STATUS_META).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}
        </select>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {isLoading ? <div style={{ padding: '2rem' }}><LoadingSpinner /></div> : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr><th>Reference</th><th>Type</th><th>Severity</th><th>Subject</th><th>Raised By</th><th>Status</th><th>Blacklisted</th><th>Date</th><th></th></tr>
              </thead>
              <tbody>
                {alerts.length === 0 ? (
                  <tr><td colSpan={9} style={{ textAlign: 'center', color: 'var(--text-3)', padding: '2rem' }}>No alerts found.</td></tr>
                ) : alerts.map(a => (
                  <tr key={a.id}>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{a.reference}</td>
                    <td style={{ fontSize: '0.8rem', textTransform: 'capitalize' }}>{a.alert_type.replace(/_/g, ' ')}</td>
                    <td><SeverityDot value={a.severity} /></td>
                    <td style={{ fontSize: '0.8rem' }}>{a.subject_email || '—'}</td>
                    <td style={{ fontSize: '0.8rem' }}>{a.raised_by_email}</td>
                    <td><StatusBadge value={a.status} /></td>
                    <td style={{ textAlign: 'center' }}>
                      {a.is_blacklisted ? <span style={{ color: '#FF4757', fontWeight: 700, fontSize: '0.75rem' }}>YES</span> : <span style={{ color: 'var(--text-3)', fontSize: '0.75rem' }}>No</span>}
                    </td>
                    <td style={{ fontSize: '0.8rem', color: 'var(--text-2)' }}>{formatDate(a.created_at)}</td>
                    <td>
                      {a.status !== 'dismissed' && a.status !== 'confirmed' && (
                        <button className="btn btn-ghost" style={{ fontSize: '0.75rem', padding: '0.3rem 0.75rem' }}
                          onClick={() => setSelected(a)}>
                          Update
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && <UpdateModal alert={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
